import { Injectable } from '@angular/core';
import { Product } from '@shared/models/product.model';
import { SeperatedColumn } from '@shared/models/seperated-columns';
import { DateService } from '../date.service';
import { ImportOrderTransaction } from '@usf/ngrx-order/lib/reducers/import-orders/import-orders.reducer';
import {
  INVALID_ORDER_ITEM,
  ImportOrderValidationErrorTypeEnum,
} from '@order/pages/import-order-validation/constants/import-order-validation.constants';
import { Customer } from '@usf/customer-types';

@Injectable({
  providedIn: 'root',
})
export class ImportOrderDownloadDtoService {
  /**
   *
   * @param products Contains product map
   * @param importedOrders Contains ImportOrderTransaction array
   * @param customers Contains customer array
   * @returns
   */
  static createMultipleSeparatedColumnsForImportOrders(
    products: Map<number, Product>,
    importedOrders: ImportOrderTransaction[],
    customers: Customer[],
  ): SeperatedColumn[] {
    const flatMap = arrays => {
      return !!arrays ? [].concat.apply([], arrays) : [];
    };
    let customerNumbers = [];
    let customerNames = [];
    let poNumbers = [];
    let deliveryDates = [];
    let productNumbers = [];
    let productDescriptions = [];
    let productBrands = [];
    let productSizes = [];
    let csUnitsTotals = [];
    let eaUnitsTotals = [];
    let errorMessage = [];

    importedOrders.map(order => {
      order.orderItems?.map(oi => {
        //Only proceed if order or order item has invalid error code
        if (INVALID_ORDER_ITEM.includes(order?.orderHeaderErrorCode)) {
          INVALID_ORDER_ITEM.includes(oi?.orderItemErrorCode)
            ? errorMessage.push(
                `"${
                  this.escapeSeperatedValues(order.orderHeaderErrorMsg) +
                  '  |  ' +
                  this.escapeSeperatedValues(oi.orderItemErrorMsg)
                }"`,
              )
            : errorMessage.push(
                `"${this.escapeSeperatedValues(order.orderHeaderErrorMsg)}"`,
              );
        } else if (INVALID_ORDER_ITEM.includes(oi?.orderItemErrorCode)) {
          errorMessage.push(
            `"${this.escapeSeperatedValues(oi.orderItemErrorMsg)}"`,
          );
        } else {
          return;
        }

        customerNumbers.push(order.customerNumber || '');
        customerNames.push(
          `"${
            customers.find(
              customer => customer?.customerNumber === order?.customerNumber,
            )?.customerName || ''
          }"`,
        );
        deliveryDates.push(
          order?.confirmedDeliveryDate
            ? DateService.formatDateMaintainUTC(order.confirmedDeliveryDate)
            : '',
        );
        poNumbers.push(order.purchaseOrderNumber || '');
        productNumbers.push(`"${oi.productNumber}"`);
        productDescriptions.push(
          `"${
            this.escapeSeperatedValues(
              products.get(oi.productNumber)?.summary?.productDescTxtl,
            ) || ''
          }"`,
        );
        productBrands.push(
          `"${
            this.escapeSeperatedValues(
              products.get(oi.productNumber)?.summary?.brand,
            ) || ''
          }"`,
        );
        productSizes.push(
          `"${
            this.escapeSeperatedValues(
              products.get(oi.productNumber)?.summary?.salesPackSize,
            ) || ''
          }"`,
        );
        csUnitsTotals.push(`"${oi.unitsOrdered || 0}"`);
        eaUnitsTotals.push(`"${oi.eachesOrdered || 0}"`);
      });
    });

    const separatedColumns: SeperatedColumn[] = [
      {
        columnName: 'CUSTOMER #',
        columnValues: flatMap(customerNumbers),
        totals: '',
      },
      {
        columnName: 'CUSTOMER NAME',
        columnValues: flatMap(customerNames),
        totals: '',
      },
      {
        columnName: 'PO #',
        columnValues: flatMap(poNumbers),
        totals: '',
      },
      {
        columnName: 'DELIVERY DATE',
        columnValues: flatMap(deliveryDates),
        totals: '',
      },
      {
        columnName: 'PRODUCT #',
        columnValues: flatMap(productNumbers),
        totals: '',
      },
      {
        columnName: 'PRODUCT DESCRIPTION',
        columnValues: flatMap(productDescriptions),
        totals: '',
      },
      {
        columnName: 'BRAND',
        columnValues: flatMap(productBrands),
        totals: '',
      },
      {
        columnName: 'PACK SIZE',
        columnValues: flatMap(productSizes),
        totals: '',
      },
      {
        columnName: 'CASES',
        columnValues: flatMap(csUnitsTotals),
        totals: '',
      },
      {
        columnName: 'EACHES',
        columnValues: flatMap(eaUnitsTotals),
        totals: '',
      },
      {
        columnName: 'ERROR MESSAGE',
        columnValues: flatMap(errorMessage),
        totals: '',
      },
    ];
    return separatedColumns;
  }

  private static escapeSeperatedValues(value: string): string {
    return value?.replace(`"`, `""`);
  }
}
